import { EWorkshopChildcareAgeGroups } from '@packages/utils';
import {
  EAgeGroup,
  EWorkshopLanguage,
  TAddressShortDataDB,
  TAddressShortDataUI,
  TAddressTypesDB,
  TAddressTypesUI,
  TAllowedOperationsDB,
  TAllowedOperationsUI,
  TAllowedOperationsValueDB,
  TAllowedOperationsValueUI,
  TLanguageOptionUI,
  TShortAddressWithAllowOperationsDB,
  TShortAddressWithAllowOperationsUI,
  EDateSortParams,
} from './generic.types';
import { TTagDB, TTagUI } from './tags.types';

export enum EEventStatus {
  PLANNING = 'PLANNING',
  PUBLISHED = 'PUBLISHED',
  CANCELLED = 'CANCELLED',
  CLOSED = 'CLOSED',
  RESCHEDULED = 'RESCHEDULED',
}

export enum EEventCategory {
  STANDALONE = 'STANDALONE',
  SERIES_WITH_THREE_PARTS = 'SERIES_WITH_THREE_PARTS',
  SERIES_WITH_TWO_PARTS = 'SERIES_WITH_TWO_PARTS',
}

export enum EAttendanceType {
  IN_PERSON = 'IN_PERSON',
  VIRTUAL = 'VIRTUAL',
}

export enum EEventPartType {
  STANDALONE = 'STANDALONE',
  PART_ONE = 'PART_ONE',
  PART_TWO = 'PART_TWO',
  PART_THREE = 'PART_THREE',
}

export enum EEventStandaloneType {
  READY_FOR_MATH = 'READY_FOR_MATH',
  READY_FOR_KINDERGARTEN_SOCIAL_EMOTIONAL = 'READY_FOR_KINDERGARTEN_SOCIAL_EMOTIONAL',
  CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1 = 'CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1',
  CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2 = 'CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2',
}

export enum EEventType {
  BOOK_FAIR = 'BOOK_FAIR',
  COMMUNITY_EVENT = 'COMMUNITY_EVENT',
  CONFERENCE_ATTENDANCE = 'CONFERENCE_ATTENDANCE',
  CONFERENCE_HOST = 'CONFERENCE_HOST',
  CONFERENCE_PRESENTATION = 'CONFERENCE_PRESENTATION',
  CONFERENCE_VENDOR_TABLE = 'CONFERENCE_VENDOR_TABLE',
  EARLY_LEARNING_FAIR = 'EARLY_LEARNING_FAIR',
  FUNDRAISER = 'FUNDRAISER',
  IDAHO_AEYC_SYMPOSIUM = 'IDAHO_AEYC_SYMPOSIUM',
  KINDERGARTEN_REGISTRATION = 'KINDERGARTEN_REGISTRATION',
  PARENT_CAFE = 'PARENT_CAFE',
  PLC_EVENT = 'PLC_EVENT',
  PRESCHOOL_INFO_NIGHT = 'PRESCHOOL_INFO_NIGHT',
  PRIVATE_EVENT = 'PRIVATE_EVENT',
  PUBLIC_EVENT = 'PUBLIC_EVENT',
  TRAINING = 'TRAINING',
  OTHER = 'OTHER',
}

export enum EEventVisibilityGroup {
  AEYC = 'AEYC',
  PUBLIC = 'PUBLIC',
}

/** Maps each event type to its visibility group (AEYC or PUBLIC) */
export const EVENT_TYPE_VISIBILITY: Record<string, EEventVisibilityGroup> = {
  [EEventType.PARENT_CAFE]: EEventVisibilityGroup.AEYC,
  [EEventType.BOOK_FAIR]: EEventVisibilityGroup.PUBLIC,
  [EEventType.COMMUNITY_EVENT]: EEventVisibilityGroup.PUBLIC,
  [EEventType.CONFERENCE_ATTENDANCE]: EEventVisibilityGroup.PUBLIC,
  [EEventType.CONFERENCE_HOST]: EEventVisibilityGroup.PUBLIC,
  [EEventType.CONFERENCE_PRESENTATION]: EEventVisibilityGroup.PUBLIC,
  [EEventType.CONFERENCE_VENDOR_TABLE]: EEventVisibilityGroup.PUBLIC,
  [EEventType.EARLY_LEARNING_FAIR]: EEventVisibilityGroup.PUBLIC,
  [EEventType.FUNDRAISER]: EEventVisibilityGroup.PUBLIC,
  [EEventType.KINDERGARTEN_REGISTRATION]: EEventVisibilityGroup.PUBLIC,
  [EEventType.OTHER]: EEventVisibilityGroup.PUBLIC,
  [EEventType.PLC_EVENT]: EEventVisibilityGroup.PUBLIC,
  [EEventType.PRESCHOOL_INFO_NIGHT]: EEventVisibilityGroup.PUBLIC,
  [EEventType.PRIVATE_EVENT]: EEventVisibilityGroup.PUBLIC,
  [EEventType.PUBLIC_EVENT]: EEventVisibilityGroup.PUBLIC,
};

export enum EEventSeriesType {
  READY_FOR_KINDERGARTEN = 'READY_FOR_KINDERGARTEN',
  LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS = 'LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS',
}

export enum EEventSortParams {
  CREATION_DATE = 'CREATION_DATE',
  // Shadow fields for the Event Date column (backend EventSortFiled)
  NEXT_SESSION_DATE = EDateSortParams.NEXT_SESSION_DATE,
  LAST_SESSION_DATE = EDateSortParams.LAST_SESSION_DATE,
}
export enum EEventFilterParams {
  STATUS = 'statuses',
  TYPE = 'types',
  HOST = 'hostIds',
  CITY = 'cities',
  LOCATION = 'locationIds',
  TAGS = 'tagIds',
  HOSTING_TYPES = 'hostingTypes',
  ATTENDANCE_TYPE = 'attendanceTypes',
  SITE_COORDINATOR = 'siteCoordinator',
  TITLE = 'title',
  ELC = 'elcIds',
  CAPACITY = 'capacity',
  NEAR_CAPACITY = 'nearCapacity',
  OVER_CAPACITY = 'overCapacity',
}

// Sentinel value used in the ELC filter dropdown to represent the
// "Not in an ELC" option. The server expects `includeNoElc: true`
// rather than a real UUID, so we strip this sentinel out of `elcIds`
// in the filter converter (see eventFilterConverter).
export const EVENT_NO_ELC_FILTER_VALUE = '__NO_ELC__';

// Body of POST /events/export (GROW-4162): the same filters as the list GET,
// plus the visible column ids and the active sort.
export type TExportEventsFilters = {
  eventIds?: string[];
  hostIds?: string[];
  elcIds?: string[];
  includeNoElc?: boolean;
  locationIds?: string[];
  cities?: string[];
  types?: string[];
  attendanceTypes?: string[];
  statuses?: string[];
  hostingTypes?: string[];
  siteCoordinator?: string;
  title?: string;
  tagIds?: string[];
  myEvents?: boolean;
  nearCapacity?: boolean;
  overCapacity?: boolean;
};

export type TExportEventsRequest = {
  columns?: string[];
  filters: TExportEventsFilters;
  sort?: {
    field: string;
    direction: string;
  };
};

export enum ECapacityEmptyTexts {
  UNLIMITED_LONG = 'UNLIMITED_LONG',
  UNLIMITED_SHORT = 'UNLIMITED_SHORT',
  LIMITED_WITH_EVENT = 'LIMITED_WITH_EVENT',
}

export type TEventHostDataUI = {
  id: string;
  name: string;
  address: TAddressShortDataUI;
  allowedOperations: TAllowedOperationsUI;
  hostType?: 'ORGANIZATION' | 'ELC';
};
export type TEventHostDataDB = {
  id: string;
  name: string;
  address: TAddressShortDataDB;
  allowedOperations: TAllowedOperationsDB;
  hostType?: 'ORGANIZATION' | 'ELC';
};

export type TEventLocationUI = {
  id: string;
  name: string;
  address: TAddressShortDataUI;
  nameAndAddress: string;
  allowedOperations: TAllowedOperationsUI;
};
export type TEventLocationDB = {
  id: string;
  name: string;
  address: TAddressShortDataDB;
  allowedOperations: TAllowedOperationsDB;
};

export type TEventSiteCoordinatorUI = {
  allowedOperations: TAllowedOperationsUI;
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  phone?: string;
  profilePicture?: string;
  fullName?: string;
};
export type TEventSiteCoordinatorDB = {
  allowedOperations: TAllowedOperationsDB;
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  phone?: string;
  profilePicture?: string;
};

export type TPhotoGalleryUI = {
  originalPhoto: string;
  thumbnailPhoto: string;
};

export type TPhotoGalleryDB = {
  originalPhoto: string;
  thumbnailPhoto: string;
};

export type TEventGalleryUI = {
  photos: TPhotoGalleryUI[];
};

export type TEventGalleryDB = {
  photos: TPhotoGalleryDB[];
};

export type TAddEventPhotoUI = {
  eventId: string;
  photoUrl: string;
};

export type TAddEventPhotoDB = {
  eventId: string;
  photoUrl: string;
};

export type TEventRegistrantsUrl = {
  eventId: string;
  localDate: string;
};

export type TSessionsUI = {
  facilitator: TEventSiteCoordinatorUI;
  id: string;
  partType: EEventPartType;
};

export type TSessionsDB = {
  facilitator: TEventSiteCoordinatorDB;
  id: string;
  partType: EEventPartType;
};

export type TCapacityUI = {
  allowedOperations: TAllowedOperationsUI;
  value: string;
  showValue?: string;
};
export type TCapacityDB = {
  allowedOperations: TAllowedOperationsDB;
  value: string;
};

export type TClassItemUI = {
  ageGroup: TAgeGroupAllowedOperationsValueUI;
  capacity: TCapacityUI;
  id: string;
  language: TLanguageAllowedOperationsValueUI;
  sessions: TSessionsUI[];
  registrantsCount: number;
};

export type TClassItemDB = {
  ageGroup: TAgeGroupAllowedOperationsValueDB;
  capacity: TCapacityDB;
  id: string;
  language: TLanguageAllowedOperationsValueDB;
  sessions: TSessionsDB[];
  registrantsCount: number;
};

export type TClassesDataUI = {
  allowedOperations: TAllowedOperationsUI;
  value?: TClassItemUI[];
};

export type TClassesDataDB = {
  allowedOperations: TAllowedOperationsDB;
  value?: TClassItemDB[];
};

export type TPossibleAgeGroupUI = {
  value: EAgeGroup;
  label: EAgeGroup;
};

export type TFlyersDataUI = {
  existingAgeGroups: EAgeGroup[];
  parts: TPartsUI[];
};

export enum EEventDetailsAllowedActions {
  // actions came from BE
  DELETE = 'DELETE',
  CLOSE = 'CLOSE',
  PUBLISH = 'PUBLISH',
  DUPLICATE = 'DUPLICATE',
  CANCEL = 'CANCEL',
  RESCHEDULE = 'RESCHEDULE',
  REPUBLISH = 'REPUBLISH',
  // actions added on FE
  DOWNLOAD_LANDING_QR = 'DOWNLOAD_LANDING_QR',
  COPY_URL = 'COPY_URL',
}

export type TPartsUI = {
  date?: TAllowedOperationsValueUI<string> | null;
  isPassed: boolean;
  partType: EEventPartType;
  eventPartId: string;
  startTime?: string;
  endTime?: string;
};

export type TPartsDB = {
  date?: TAllowedOperationsValueDB<string> | null;
  isPassed: boolean;
  partType: EEventPartType;
  eventPartId: string;
  startTime?: string;
  endTime?: string;
};

export type TEventDetailsDataUI = {
  actualSessionDates: string[];
  allowedActions: EEventDetailsAllowedActions[];
  attendanceType: EAttendanceType;
  capacity: TCapacityUI;
  childcareProvided: TAllowedOperationsValueUI<boolean> | null;
  classes: TClassesDataUI;
  customQuestion: TAllowedOperationsValueUI<string>;
  description?: TAllowedOperationsValueUI<string>;
  foodProvided: TAllowedOperationsValueUI<boolean>;
  host: TEventHostDataUI;
  hostedPrivately: boolean;
  id: string;
  isOwner?: boolean;
  location: TEventLocationUI;
  locationSameAsHost: boolean;
  meetingLink: TAllowedOperationsValueUI<string> | null;
  photo: TAllowedOperationsValueUI<string>;
  possibleAgeGroups: EAgeGroup[];
  shipmentAddress: TShortAddressWithAllowOperationsUI;
  shipmentLocation: TEventLocationUI;
  shipmentAddressSameAsLocationAddress: boolean;
  siteCoordinators: TEventSiteCoordinatorUI[];
  eventCoordinators: TEventSiteCoordinatorUI[];
  status: EEventStatus;
  type: EEventStandaloneType | EEventSeriesType;
  typeCategory: EEventCategory;
  hasStartedPart: boolean;
  parts?: TPartsUI[];
  flyerData: TFlyersDataUI;
  isCCP: boolean;
  timezone?: string;
  language?: string;
  attendees?: string;
  requiresRegistration?: boolean;
  title?: string;
  allowRegistration?: boolean;
};

export type TEventDetailsDataDB = {
  actualSessionDates: string[];
  allowedActions: EEventDetailsAllowedActions[];
  attendanceType: EAttendanceType;
  capacity: TCapacityDB;
  childcareProvided: TAllowedOperationsValueDB<boolean>;
  classes: TClassesDataDB;
  customQuestion: TAllowedOperationsValueDB<string>;
  description?: TAllowedOperationsValueDB<string>;
  foodProvided: TAllowedOperationsValueDB<boolean>;
  host: TEventHostDataDB;
  hostedPrivately: boolean;
  id: string;
  isOwner?: boolean;
  location: TEventLocationDB;
  locationSameAsHost: boolean;
  meetingLink: TAllowedOperationsValueDB<string> | null;
  photo: TAllowedOperationsValueDB<string>;
  possibleAgeGroups: EAgeGroup[];
  shipmentAddress: TShortAddressWithAllowOperationsDB;
  shipmentLocation: TEventLocationDB;
  shipmentAddressSameAsLocationAddress: boolean;
  siteCoordinators: { value: TEventSiteCoordinatorDB[] };
  eventCoordinators: { value: TEventSiteCoordinatorDB[] };
  status: EEventStatus;
  type: EEventStandaloneType | EEventSeriesType;
  typeCategory: EEventCategory;
  hasStartedPart: boolean;
  parts: TPartsDB[];
  timezone?: string;
  language?: string;
  attendees?: string;
  requiresRegistration?: boolean;
  title?: string;
  allowRegistration?: boolean;
};

export type TEventChildcareRequirementUI = {
  ageGroup: EWorkshopChildcareAgeGroups;
  childCount: number;
};

export type TAddEventRegistrationUI = {
  eventId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  allowedTakePicture?: boolean;
  customQuestionAnswer?: string;
  childcareRequirements?: TEventChildcareRequirementUI[];
};

export type TEventChildCareUI = {
  ageGroup: EWorkshopChildcareAgeGroups;
  count: string;
};

export type TEventChildCareDB = {
  ageGroup: EWorkshopChildcareAgeGroups;
  count: string;
};

export type TEventSessionDataUI = {
  partType: EEventPartType;
  date: string;
  facilitator: string;
};

export type TEventSessionDataDB = {
  partType: EEventPartType;
  dateTime?: string;
  facilitatorId?: string;
};

export type TEventClassDataUI = {
  eventId: string;
  age: EAgeGroup;
  language: EWorkshopLanguage;
  capacity?: string;
  sessions: TEventSessionDataUI[];
};

export type TEventClassDataDB = {
  eventId: string;
  ageGroup: EAgeGroup;
  language: EWorkshopLanguage;
  capacity?: string;
  sessions: TEventSessionDataDB[];
};

export type TDuplicateEventUI = {
  eventId: string;
};

export type TDuplicateEventDB = {
  eventId: string;
};

export type TDeleteEventClassUI = {
  classId: string;
  eventId: string;
};

export type TDeleteEventClassDB = {
  classId: string;
  eventId: string;
  description?: TAllowedOperationsValueDB<string>;
};

export type TEventCreateUI = {
  type: EEventSeriesType | EEventStandaloneType;
  title?: string;
  host: string;
  hostType: string;
  attendanceType: EAttendanceType;
  numberOfDays?: number;
  hostedPrivately: boolean;
  requiresRegistration?: boolean;
};

export type TEventCreateDB = {
  type: EEventSeriesType | EEventStandaloneType;
  title?: string;
  hostId?: string;
  elcId?: string;
  attendanceType: EAttendanceType;
  numberOfDays?: number;
  hostedPrivately: boolean;
  requiresRegistration?: boolean;
};

export type TEventSessionItemUI = {
  ageGroup: EAgeGroup;
  language: EWorkshopLanguage;
  count: number;
};
export type TEventSessionItemDB = {
  ageGroup: EAgeGroup;
  language: EWorkshopLanguage;
  count: number;
};

export type TEventSessionDateUI = {
  partType: EEventPartType;
  date: string | null;
};
export type TEventSessionDateDB = Record<EEventPartType, string | null>;

export type TEventDataUI = {
  eventId: string;
  hostName: string;
  hostId: string;
  hostedPrivately: boolean;
  attendanceType: EAttendanceType;
  createdDate: string;
  city: string;
  eventType: EEventStandaloneType | EEventSeriesType;
  eventStatus: EEventStatus;
  eventCategory: EEventCategory;
  englishSessions?: TEventSessionItemUI[];
  spanishSessions?: TEventSessionItemUI[];
  tags?: TTagUI[];
  locationId: string;
  locationName: string;
  capacity: string | null;
  registrantCount: number;
  attendanceCount: number;
  hasWaitList: boolean;
  sessionDates: TEventSessionDateUI[];
  /** Part holding the next upcoming session (GROW-4274); null once all are past */
  nextSessionPart?: EEventPartType | null;
  siteCoordinators?: string[];
  eventCoordinators?: string[];
  title?: string;
  requiresRegistration?: boolean;
  elcs: TEventLocationElcUI[];
};
export type TEventLocationElcUI = {
  id: string;
  name: string;
};
export type TEventDataDB = {
  eventId: string;
  hostName: string;
  hostId: string;
  hostedPrivately: boolean;
  attendanceType: EAttendanceType;
  createdDate: string;
  city: string;
  eventType: EEventStandaloneType | EEventSeriesType;
  eventStatus: EEventStatus;
  eventCategory: EEventCategory;
  englishSessions?: TEventSessionItemDB[];
  spanishSessions?: TEventSessionItemDB[];
  tags?: TTagDB[];
  locationId: string;
  locationName: string;
  capacity: string | null;
  registrantCount: number;
  attendanceCount: number;
  hasWaitList: boolean;
  sessionDates: TEventSessionDateDB;
  nextSessionPart?: EEventPartType | null;
  siteCoordinators?: string[];
  eventCoordinators?: string[];
  title?: string;
  requiresRegistration?: boolean;
  elcs: TEventLocationElcDB[];
};
export type TEventLocationElcDB = {
  id: string;
  name: string;
};

export type TAgeGroupAllowedOperationsValueUI = {
  allowedOperations: TAllowedOperationsUI;
  value: TPossibleAgeGroupUI;
};

export type TAgeGroupAllowedOperationsValueDB = {
  allowedOperations: TAllowedOperationsDB;
  value: EAgeGroup;
};

export type TLanguageAllowedOperationsValueUI = {
  allowedOperations: TAllowedOperationsUI;
  value: TLanguageOptionUI;
};

export type TLanguageAllowedOperationsValueDB = {
  allowedOperations: TAllowedOperationsDB;
  value: EWorkshopLanguage;
};

export type TEventClassAgeGroupEditUI = {
  id: string;
  eventId: string;
  value: string;
};
export type TEventClassAgeGroupEditDB = {
  eventClassId: string;
  eventId: string;
  ageGroup: string;
};
export type TEventTimezoneEditUI = {
  eventId: string;
  timezone: string;
};
export type TEventTimezoneEditDB = {
  eventId: string;
  timezone: string;
};
export type TEventLanguageEditUI = {
  eventId: string;
  language: EWorkshopLanguage;
};
export type TEventLanguageEditDB = {
  eventId: string;
  language: EWorkshopLanguage;
};
export type TEventClassLanguageEditUI = {
  id: string;
  eventId: string;
  value: EWorkshopLanguage;
};
export type TEventClassLanguageEditDB = {
  eventClassId: string;
  eventId: string;
  language: EWorkshopLanguage;
};

export type TEventClassDateTimeEditUI = {
  id: string;
  eventId: string;
  eventSessionId: string;
  value: string;
};
export type TEventClassDateTimeEditDB = {
  eventClassId: string;
  eventId: string;
  eventSessionId: string;
  date: string;
};
export type TEventPartDateTimeEditDB = {
  eventId: string;
  eventPartId: string;
  date: string;
};
export type TEventPartDateTimeEditUI = {
  eventId: string;
  eventPartId: string;
  dateTime: string;
};
export type TEventPartTimeEditUI = {
  eventId: string;
  eventPartId: string;
  time: string;
  timeType: 'START_TIME' | 'END_TIME';
};

export type TEventPartTimeEditDB = {
  eventId: string;
  eventPartId: string;
  time: string;
  timeType: 'START_TIME' | 'END_TIME';
};

export type TEventClassFacilitatorEditUI = {
  id: string;
  eventId: string;
  eventSessionId: string;
  value: string | null;
};

export type TEventClassFacilitatorEditDB = {
  eventClassId: string;
  eventId: string;
  eventSessionId: string;
  userId: string | null;
};

export type TClassCapacityEditUI = {
  id: string;
  eventId: string;
  value: string;
};
export type TClassCapacityEditDB = {
  eventClassId: string;
  eventId: string;
  capacity: string;
};
export type TEventAttendanceEditUI = {
  eventId: string;
  attendees: number;
};
export type TEventCapacityEditUI = {
  eventId: string;
  value: string;
};
export type TEventCapacityEditDB = {
  eventId: string;
  capacity: string | null;
};

export type TEventLocationEditUI = {
  id: string;
  value: string;
};
export type TEventLocationEditDB = {
  eventId: string;
  locationId: string;
};
export type TEventHostEditUI = {
  id: string;
  value: string | null;
};
export type TEventHostEditDB = {
  eventId: string;
  hostId: string | null;
};

export type TEventSiteCoordinatorEditUI = {
  id: string;
  value: string[];
};

export type TEventSiteCoordinatorEditDB = {
  eventId: string;
  userIds: string[];
};

export type TEventCoordinatorEditUI = {
  id: string;
  value: string[];
};

export type TEventCoordinatorEditDB = {
  eventId: string;
  userIds: string[];
};

export type TEventFoodProvidedEditUI = {
  id: string;
  value: boolean;
};
export type TEventFoodProvidedEditDB = {
  eventId: string;
  foodProvided: boolean;
};

export type TEventMeetingLinkEditUI = {
  id: string;
  value: string | null;
};
export type TEventMeetingLinkEditDB = {
  eventId: string;
  meetingLink: string | null;
};

export type TEventChildcareProvidedEditUI = {
  id: string;
  value: boolean;
};
export type TEventChildcareProvidedEditDB = {
  eventId: string;
  childcareProvided: boolean;
};

export type TEventShippingAddressEditUI = TAddressTypesUI & {
  id: string;
};
export type TEventShippingAddressEditDB = {
  eventId: string;
  address: TAddressTypesDB;
};

export type TEventLocationAddressEditUI = {
  eventId: string;
  address: TAddressTypesUI;
};
export type TEventLocationAddressEditDB = {
  eventId: string;
  address: TAddressTypesDB;
};

export type TEventShipmentLocationEditUI = {
  id: string;
  value: string;
};
export type TEventShipmentLocationEditDB = {
  eventId: string;
  locationId: string;
};

export type TEventDescriptionEditUI = {
  id: string;
  value: string;
};
export type TEventDescriptionEditDB = {
  eventId: string;
  description: string;
};

export type TEventTitleEditUI = {
  id: string;
  value: string;
};
export type TEventTitleEditDB = {
  eventId: string;
  title: string;
};

export type TEventTypeEditUI = {
  id: string;
  value: string;
};
export type TEventTypeEditDB = {
  eventId: string;
  type: string;
};

export type TUpdateCustomQuestionUi = { id: string; question: string };

export type TEventsNoteUI = {
  eventId: string;
  noteId?: string;
  note?: string;
};

export type TEventsNoteDB = {
  eventId: string;
  noteId?: string;
  note?: string;
};

export type TEventPartKitOrderItemUI = {
  kitId: string;
  name: string;
  inventory?: number;
  suggestedOrder: number;
  totalOrders: number;
  orderDate: string | null;
  kitOrderId?: string | null;
};

export type TEventPartKitOrdersUI = {
  eventId: string;
  items: Record<EEventPartType, TEventPartKitOrderItemUI[]>;
};

export type TEventPartKitOrderItemDB = {
  kitId: string;
  name: string;
  inventory?: number;
  suggestedOrder: number;
  totalOrders: number;
  orderDate: string | null;
  kitOrderId?: string | null;
};

export type TEventPartKitOrdersDB = {
  eventId: string;
  items: Record<string, TEventPartKitOrderItemDB[]>;
};

export type TEventKitOrderHistoryItem = {
  eventKitId: string;
  kitName: string;
  orderCount: number;
  orderedBy: string;
  orderedByName: string;
  orderDate: string;
  orderChange: number;
  operation: string;
};

export type TEventKitOrderHistoryResponse = {
  orderHistory: {
    [key in EEventPartType]?: {
      [date: string]: TEventKitOrderHistoryItem[];
    };
  };
};
